import {
  IconCalendar,
  IconCalendarTime,
  IconChevronDown,
  IconClipboardList,
  IconCreditCard,
  IconDownload,
  IconFile,
  IconFileText,
  IconFolders,
  IconLink,
  IconMapPin,
  IconNote,
  IconPaperclip,
  IconTags,
  IconTruck,
  IconUpload,
  IconUser,
  IconUserCheck,
  IconWallet,
  IconX,
} from "@tabler/icons-react";
import { Button, Select, TextInput, Textarea, Tooltip } from "@mantine/core";
import { RfqField, RfqSection } from "./RfqSection";
import type { RfqAttachment, RfqLine, RfqRole } from "@/lib/rfq/types";
import { useMemo, useRef, useState } from "react";

import type { RfqHeader } from "@/lib/rfq/api";
import { fmtMoney } from "@/lib/rfq/calc";
import { resolveWeight } from "@/lib/rfq/calc";
import { useRfqLabels } from "@/lib/rfq/labels";

export interface RfqHeaderPatch {
  name?: string;
  issueDate?: string;
  deliveryLocation?: string;
  paymentTerm?: string;
  priceBasis?: string;
  remark?: string;
}

function UnsupportedField({
  icon,
  label,
  hint,
}: {
  icon?: any;
  label: string;
  hint: string;
}) {
  return (
    <Tooltip label={hint} withArrow multiline w={260}>
      <div className="opacity-60">
        <RfqField icon={icon} label={label} />
      </div>
    </Tooltip>
  );
}

function RfqInfoCard({
  header,
  lines,
  role,
  editable,
  onSave,
  onAttachmentsChange,
  onUploadAttachment,
}: {
  header: RfqHeader;
  lines: RfqLine[];
  role: RfqRole;
  editable: boolean;
  onSave: (patch: RfqHeaderPatch) => Promise<void> | void;
  onAttachmentsChange: (attachments: RfqAttachment[]) => Promise<void> | void;
  onUploadAttachment: (file: File) => Promise<RfqAttachment>;
}) {
  const { t, category, attachKind, calc } = useRfqLabels();
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draft, setDraft] = useState<RfqHeaderPatch>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const unsupportedHint =
    t("rfq.unsupportedField") || "後端尚未提供此欄位，暫時無法儲存";

  const value = <K extends keyof RfqHeaderPatch>(key: K): string =>
    (draft[key] as string | undefined) ?? ((header[key] as string) || "");

  const startEdit = () => {
    setDraft({});
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft({});
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setDraft({});
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: RfqAttachment[] = [];
      for (const file of Array.from(files)) {
        uploaded.push(await onUploadAttachment(file));
      }
      await onAttachmentsChange([...header.attachments, ...uploaded]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const categories = useMemo(
    () => Array.from(new Set(lines.map((line) => line.category))),
    [lines],
  );

  const budget = useMemo(
    () =>
      lines.reduce((sum, line) => {
        if (line.targetPrice == null) return sum;
        if (line.priceMode === "weight") {
          const weight = resolveWeight(line, calc);
          return sum + line.targetPrice * (weight?.kg ?? 0);
        }
        return sum + line.targetPrice * line.qty;
      }, 0),
    [lines, calc],
  );

  const canSeeTarget = role === "manager";

  return (
    <RfqSection
      icon={IconClipboardList}
      title={t("rfq.info.sectionTitle") || "詢價單資訊"}
      action={
        <div className="flex items-center gap-2">
          {open && editable ? (
            editing ? (
              <>
                <Button
                  variant="default"
                  size="xs"
                  radius="md"
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  {t("rfq.info.cancel") || "取消"}
                </Button>
                <Button
                  size="xs"
                  radius="md"
                  color="blue"
                  loading={saving}
                  onClick={save}
                >
                  {t("rfq.info.save") || "儲存"}
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                size="xs"
                radius="md"
                onClick={startEdit}
              >
                {t("rfq.info.edit") || "編輯"}
              </Button>
            )
          ) : null}
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            radius="md"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            rightSection={
              <IconChevronDown
                size={14}
                className={
                  open
                    ? "rotate-180 transition-transform"
                    : "transition-transform"
                }
              />
            }
          >
            {open
              ? t("rfq.info.collapse") || "收合"
              : t("rfq.info.expand") || "展開"}
          </Button>
        </div>
      }
    >
      {open ? (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <RfqField
              icon={IconFileText}
              label={t("rfq.info.no") || "詢價單號"}
              value={header.code}
            />
            {editing ? (
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconFileText size={13} />
                  {t("rfq.info.title") || "詢價標題"}
                </label>
                <TextInput
                  size="sm"
                  value={value("name")}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      name: event.currentTarget.value,
                    }))
                  }
                  aria-label={t("rfq.info.title") || "詢價標題"}
                />
              </div>
            ) : (
              <RfqField
                icon={IconFileText}
                label={t("rfq.info.title") || "詢價標題"}
                value={header.name}
              />
            )}
            {/* <UnsupportedField
              icon={IconFolders}
              label={t("rfq.info.project") || "專案"}
              hint={unsupportedHint}
            /> */}
            {/* <UnsupportedField
              icon={IconFileText}
              label={t("rfq.info.contract") || "合約書編號"}
              hint={unsupportedHint}
            /> */}
            {editing ? (
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconCalendar size={13} />
                  {t("rfq.info.issueDate") || "發出日期"}
                </label>
                <TextInput
                  size="sm"
                  type="date"
                  value={value("issueDate")}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      issueDate: event.currentTarget.value,
                    }))
                  }
                  aria-label={t("rfq.info.issueDate") || "發出日期"}
                />
              </div>
            ) : (
              <RfqField
                icon={IconCalendar}
                label={t("rfq.info.issueDate") || "發出日期"}
                value={header.issueDate}
              />
            )}
            {/* <UnsupportedField
              icon={IconCalendarTime}
              label={t("rfq.info.deadline") || "報價截止時間"}
              hint={unsupportedHint}
            /> */}
            {editing ? (
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconMapPin size={13} />
                  {t("rfq.info.deliverTo") || "交貨地點"}
                </label>
                <TextInput
                  size="sm"
                  value={value("deliveryLocation")}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      deliveryLocation: event.currentTarget.value,
                    }))
                  }
                  aria-label={t("rfq.info.deliverTo") || "交貨地點"}
                />
              </div>
            ) : (
              <RfqField
                icon={IconMapPin}
                label={t("rfq.info.deliverTo") || "交貨地點"}
                value={header.deliveryLocation}
              />
            )}
            {/* <UnsupportedField
              icon={IconCalendar}
              label={t("rfq.info.requireDate") || "需求交期"}
              hint={
                t("rfq.info.requireDateHint") ||
                "需求交期改為逐項設定，請於明細頁調整"
              }
            /> */}
            {editing ? (
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconCreditCard size={13} />
                  {t("rfq.info.paymentTerms") || "我方付款條件"}
                </label>
                <TextInput
                  size="sm"
                  value={value("paymentTerm")}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      paymentTerm: event.currentTarget.value,
                    }))
                  }
                  aria-label={t("rfq.info.paymentTerms") || "我方付款條件"}
                />
              </div>
            ) : (
              <RfqField
                icon={IconCreditCard}
                label={t("rfq.info.paymentTerms") || "我方付款條件"}
                value={header.paymentTerm}
              />
            )}
            {editing ? (
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconTruck size={13} />
                  {t("rfq.info.priceBasis") || "價格基準"}
                </label>
                <Select
                  size="sm"
                  data={[
                    {
                      value: "exw",
                      label: t("rfq.info.priceBasisExw") || "出廠價 EXW",
                    },
                    {
                      value: "dap",
                      label: t("rfq.info.priceBasisDap") || "送達工地 DAP",
                    },
                  ]}
                  value={value("priceBasis") || null}
                  onChange={(next) =>
                    setDraft((prev) => ({ ...prev, priceBasis: next ?? "" }))
                  }
                  aria-label={t("rfq.info.priceBasis") || "價格基準"}
                />
              </div>
            ) : (
              <RfqField
                icon={IconTruck}
                label={t("rfq.info.priceBasis") || "價格基準"}
                value={
                  header.priceBasis === "dap"
                    ? t("rfq.info.priceBasisDap") || "送達工地 DAP"
                    : header.priceBasis === "exw"
                      ? t("rfq.info.priceBasisExw") || "出廠價 EXW"
                      : header.priceBasis
                }
              />
            )}
            {canSeeTarget ? (
              <RfqField
                icon={IconWallet}
                label={t("rfq.info.budget") || "預算總額 (目標單價加總)"}
                value={
                  <span className="font-semibold text-blue-600">
                    NT$ {fmtMoney(budget)}
                  </span>
                }
              />
            ) : null}
            <RfqField
              icon={IconUser}
              label={t("rfq.info.buyer") || "承辦採購"}
              value={header.applicantName}
            />
            <RfqField
              icon={IconUserCheck}
              label={t("rfq.info.reviewer") || "審核主管"}
              value={header.reviewerName}
            />
            <RfqField
              icon={IconCalendar}
              label={t("rfq.info.reviewDate") || "審核日期"}
              value={header.reviewDate}
            />
          </div>

          <div className="border-t border-gray-100 pt-5">
            {editing ? (
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconNote size={13} />
                  {t("rfq.info.remark") || "備註"}
                </label>
                <Textarea
                  size="sm"
                  autosize
                  minRows={2}
                  value={value("remark")}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      remark: event.currentTarget.value,
                    }))
                  }
                  aria-label={t("rfq.info.remark") || "備註"}
                />
              </div>
            ) : (
              <RfqField
                icon={IconNote}
                label={t("rfq.info.remark") || "備註"}
                value={header.remark}
              />
            )}
          </div>

          <div className="flex flex-col gap-4 border-t border-gray-100 pt-5 md:flex-row md:gap-10">
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconTags size={13} />
                {t("rfq.info.categories") || "包含類別"}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {categories.length === 0 ? (
                  <span className="text-xs text-gray-400">—</span>
                ) : (
                  categories.map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
                    >
                      {category(item)}
                    </span>
                  ))
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconLink size={13} />
                {t("rfq.info.sourcePo") || "來源採購單"}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {header.sourceOrderLabels.length === 0 ? (
                  <span className="text-xs text-gray-400">—</span>
                ) : (
                  header.sourceOrderLabels.map((po) => (
                    <span
                      key={po}
                      className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600"
                    >
                      <IconLink size={12} /> {po}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-gray-100 pt-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconPaperclip size={13} />
                {t("rfq.info.attachments") || "附件（圖說 / 規範 / 材質要求）"}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(event) => handleFiles(event.target.files)}
              />
              <Button
                variant="default"
                size="xs"
                radius="md"
                loading={uploading}
                disabled={!editable}
                leftSection={<IconUpload size={14} />}
                onClick={() => fileInputRef.current?.click()}
              >
                {t("rfq.info.addAttachment") || "新增附件"}
              </Button>
            </div>
            {header.attachments.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                {t("rfq.info.noAttachment") ||
                  "尚無附件，點「新增附件」上傳圖說、規範或材質要求。"}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {header.attachments.map((file) => (
                  <div
                    key={file.id}
                    className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 transition-colors hover:border-blue-300 hover:bg-blue-50/40"
                  >
                    <IconFile size={20} className="shrink-0 text-blue-600" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium text-gray-700">
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {attachKind(file.kind)} ・ {file.size}
                      </span>
                    </div>
                    <div className="ml-auto flex shrink-0 items-center gap-0.5">
                      <a
                        href={file.id}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`${t("rfq.info.download") || "下載"} ${file.name}`}
                        className="rounded p-1 text-gray-400 transition-colors hover:bg-white hover:text-gray-700"
                      >
                        <IconDownload size={16} />
                      </a>
                      <button
                        type="button"
                        disabled={!editable}
                        aria-label={`${t("rfq.info.remove") || "移除"} ${file.name}`}
                        onClick={() =>
                          onAttachmentsChange(
                            header.attachments.filter(
                              (item) => item.id !== file.id,
                            ),
                          )
                        }
                        className="rounded p-1 text-gray-400 transition-colors hover:bg-white hover:text-red-500 disabled:opacity-40"
                      >
                        <IconX size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          {header.code} ・ {header.name} ・{" "}
          {t("rfq.info.buyer") || "承辦採購"} {header.applicantName}
        </p>
      )}
    </RfqSection>
  );
}

export default RfqInfoCard;
